import { createFileRoute } from '@tanstack/react-router';

import { GroundTruthEditorShell } from '@/features/GroundTruthHub/GroundTruthEditorShell';
import { GroundTruthPreview } from '@/features/GroundTruthHub/GroundTruthPreview';
import { SheetSplitEditor } from '@/features/GroundTruthHub/SheetSplitEditor';
import { isNotFoundError, useGtFileStatus } from '@/features/GroundTruthHub/api';
import { editorTitle } from '@/features/GroundTruthHub/fileLabels';
import { LoadError } from '@/features/GroundTruthHub/LoadError';
import { PanelSkeleton } from '@/features/GroundTruthHub/LoadingSkeletons';
import { ingestedFileIdParams } from '@/features/GroundTruthHub/routeParams';
import { useWorkbookSheetNames } from '@/features/GroundTruthHub/useWorkbookSheetNames';
import {
    GroundTruthNotFound,
    groundTruthRouteBoundaries,
} from '@/features/GroundTruthHub/GroundTruthNotFound';

/**
 * Exported (rather than kept module-private like most route components) so it
 * can be unit-tested directly with plain props, bypassing `createFileRoute`'s
 * router-context requirement — see `sheets.test.tsx`.
 */
export function SheetSplitRoute({ ingestedFileId }: { ingestedFileId: number }) {
    const {
        data: fileRow,
        isLoading,
        isError,
        error,
        refetch,
        isRefetching,
    } = useGtFileStatus(ingestedFileId);

    const ext = (fileRow?.fileName ?? '').split('.').pop()?.toLowerCase() ?? '';
    const isExcel = ext === 'xlsx' || ext === 'xls';

    const excelUrl = isExcel ? (fileRow?.presignedUrl ?? null) : null;
    const { sheetNames, onExcelSheetNames } = useWorkbookSheetNames(excelUrl, ingestedFileId);

    if (isError && isNotFoundError(error)) {
        return <GroundTruthNotFound what="This file" />;
    }

    return (
        <GroundTruthEditorShell title={editorTitle('Sheet splits', fileRow)}>
            {isLoading ? (
                <PanelSkeleton
                    rows={5}
                    className="p-4"
                    data-testid="SheetSplitRoute__loading"
                />
            ) : isError ? (
                <div className="p-4" data-testid="SheetSplitRoute__error">
                    <LoadError
                        what="this file"
                        onRetry={() => void refetch()}
                        isRetrying={isRefetching}
                    />
                </div>
            ) : (
                // Excel sources have no rendered PDF — `docId={null}` degrades
                // the preview pane to the Excel table (or its placeholder),
                // matching GroundTruthFilePage's `isPdf ? docId : null` for
                // the sheets tab. This route is directly URL-addressable, so
                // `excelPresignedUrl` must also be gated on `isExcel` — the
                // backend now signs presigned URLs for every file with an
                // object key, not just Excel ones, so an ungated pass-through
                // would try to parse a PDF as xlsx. `h-full` because the shell's
                // content box is not a flex container, so `flex-1` alone lets the
                // preview grow to the sheet's height and scroll the tabs away.
                <GroundTruthPreview
                    className="h-full"
                    docId={null}
                    excelPresignedUrl={excelUrl}
                    onExcelSheetNames={onExcelSheetNames}
                >
                    <SheetSplitEditor
                        ingestedFileId={ingestedFileId}
                        sheetNames={sheetNames}
                    />
                </GroundTruthPreview>
            )}
        </GroundTruthEditorShell>
    );
}

function SheetSplitRouteComponent() {
    const { ingestedFileId } = Route.useParams();
    return <SheetSplitRoute ingestedFileId={ingestedFileId} />;
}

export const Route = createFileRoute('/ground-truth/$ingestedFileId/sheets')({
    ...groundTruthRouteBoundaries,
    component: SheetSplitRouteComponent,
    params: ingestedFileIdParams,
});
